// src/features/portfolio-optimization-tools/multi-asset-portfolio-optimizer/MultiAssetOptimizerPage.tsx
//
// Multi-Asset Portfolio Optimizer — an interactive "What-If" cockpit for the
// 3-tier cross-asset engine (Tier-1 HRP + ML tilt -> Tier-2 Black-Litterman ->
// Tier-3 macro-regime risk overlay). The user drives capital, risk target,
// horizon and macro regime from a dark control rail; the light content area
// renders the resulting macro allocation, a wealth-projection cone and a
// Quant X-Ray decomposition of every holding, then lets them publish the book
// to TimescaleDB.
//
// Live wiring:
//   * GET /meta/bl-defaults seeds the default engine dials (fetched on mount).
//   * GET /tier3/risk-aversion recomputes the live Black-Litterman delta every
//     time the regime / transition-alert / base-delta changes.
//   * POST /builder/global/live runs the real 3-tier optimiser (see below).
//   * POST /publish/target-weights persists an approved book.
//
// Real data (thin-UI / fat-backend): `handleOptimize` sends ONLY the user's
// dials to POST /builder/global/live. The backend hydrates the heavy
// per-sleeve pricing + signals from TimescaleDB internally (reusing the
// production GlobalPortfolioService), runs the 3-tier optimiser natively and
// returns the real 20-30 weights. The KPI cards + wealth cone are then derived
// from that real allocation (illustrative forward risk model).

import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { Layout, Sidebar } from '../../../shared/ui';
import {
    buildGlobalPortfolioLive,
    fetchBlDefaults,
    fetchDynamicRiskAversion,
    savePortfolioReport,
    type CashFlowFrequency,
    type EquityCurvePoint,
    type GlobalPortfolioRow,
    type RebalanceFrequency,
    type SimulationMetrics,
} from './api';
import {
    MacroAllocationDonut,
    type MacroSlice,
} from './components/MacroAllocationDonut';
import { QuantXRayTable } from './components/QuantXRayTable';
import { HistoricalBacktestChart } from './components/HistoricalBacktestChart';
import {
    ActivityIcon,
    BrainCircuitIcon,
    CheckCircleIcon,
    DatabaseIcon,
    InfoIcon,
    LoaderIcon,
    SaveIcon,
    Settings2Icon,
    ShieldAlertIcon,
    TrendingUpIcon,
} from './components/icons';

// Strict low -> high risk order (matches the persisted risk-band names).
const RISK_PROFILES = [
    'Capital Preservation',
    'Conservative',
    'Balanced',
    'Growth',
    'Aggressive',
] as const;

// Ilmanen business-cycle regimes (backend compute_dynamic_risk_aversion).
const MACRO_REGIMES: { value: string; label: string }[] = [
    { value: 'Expansion', label: 'Expansion (Risk-On)' },
    { value: 'Recovery', label: 'Recovery (Early-Cycle)' },
    { value: 'Slowdown', label: 'Slowdown (Stagflation)' },
    { value: 'Contraction', label: 'Contraction (Risk-Off)' },
];

// Share of capital deployed into risky sleeves (remainder is implicit cash).
// Maps the UI's strategic risk band -> the domain builder's
// `total_risky_budget` dial (the builder has no notion of a "risk profile").
const BUDGET_BY_PROFILE: Record<string, number> = {
    'Capital Preservation': 0.4,
    Conservative: 0.6,
    Balanced: 0.78,
    Growth: 0.9,
    Aggressive: 1.0,
};

// Aggregate the real per-holding book into per-sleeve macro weights for the
// donut. `sum(target_weight)` over a sleeve equals its Tier-2 macro weight
// (Tier-1 micro weights sum to ~1 within the sleeve); normalise to 100% so the
// donut reads as the composition of the risky book.
const deriveMacroWeights = (rows: GlobalPortfolioRow[]): MacroSlice[] => {
    const bySleeve: Record<string, number> = {};
    rows.forEach((r) => {
        bySleeve[r.asset_class] = (bySleeve[r.asset_class] ?? 0) + r.target_weight;
    });
    const total = Object.values(bySleeve).reduce((a, b) => a + b, 0) || 1;
    return Object.entries(bySleeve)
        .map(([name, w]) => ({ name, value: (w / total) * 100 }))
        .filter((m) => m.value >= 0.05)
        .sort((a, b) => b.value - a.value);
};

interface KpiCardProps {
    label: string;
    value: string;
    icon: ReactNode;
    tone: string;
}

const KpiCard = ({ label, value, icon, tone }: KpiCardProps) => (
    <div className="surface-card !p-4 flex items-center gap-4">
        <div className={`p-3 rounded-xl ${tone}`}>{icon}</div>
        <div>
            <p className="muted text-[10px] font-bold uppercase tracking-wider">
                {label}
            </p>
            <p className="mt-0.5 text-2xl font-black tracking-tight text-slate-900 dark:text-white font-mono">
                {value}
            </p>
        </div>
    </div>
);

export const MultiAssetOptimizerPage = () => {
    const navigate = useNavigate();

    // --- Loading / status ---
    const [loading, setLoading] = useState(false);
    const [buildError, setBuildError] = useState<string | null>(null);
    const [metaError, setMetaError] = useState<string | null>(null);

    // --- Controls ---
    const [investment, setInvestment] = useState<number>(1_000_000);
    const [riskProfile, setRiskProfile] = useState<string>('Balanced');
    const [macroRegime, setMacroRegime] = useState<string>('Expansion');
    const [isTransition, setIsTransition] = useState<boolean>(false);
    const [tiltScalar, setTiltScalar] = useState<number>(0.5);

    // --- Simulation & backtest dials ---
    const [additionalInvestment, setAdditionalInvestment] = useState<number>(0);
    const [cashFlowFreq, setCashFlowFreq] = useState<CashFlowFrequency>('none');
    const [rebalanceFreq, setRebalanceFreq] = useState<RebalanceFrequency>('monthly');
    const [taxRatePct, setTaxRatePct] = useState<number>(15.0);
    const [feeBps, setFeeBps] = useState<number>(5.0);

    // --- Meta / engine dials ---
    const [baseDelta, setBaseDelta] = useState<number>(2.5);
    const [liveDelta, setLiveDelta] = useState<number>(2.5);

    // --- Results ---
    const [portfolio, setPortfolio] = useState<GlobalPortfolioRow[]>([]);
    const [macroWeights, setMacroWeights] = useState<MacroSlice[]>([]);
    const [simulationMetrics, setSimulationMetrics] =
        useState<SimulationMetrics | null>(null);
    const [equityCurve, setEquityCurve] = useState<EquityCurvePoint[]>([]);
    const hasResult = portfolio.length > 0;

    // --- Save-report state ---
    const [savingReport, setSavingReport] = useState(false);
    const [reportName, setReportName] = useState('');
    const [saveSuccess, setSaveSuccess] = useState(false);

    // On mount: fetch the optimiser default dials (base delta + tilt) so the
    // sliders start where the live engine's own defaults sit.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const defaults = await fetchBlDefaults();
                if (cancelled) return;
                setBaseDelta(defaults.base_delta);
                setLiveDelta(defaults.base_delta);
                setTiltScalar(defaults.tilt_scalar);
                setMetaError(null);
            } catch (err) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('Failed to load optimiser defaults', err);
                setMetaError(
                    'Unable to load optimiser defaults — using engine fallbacks. '
                        + 'Base risk-aversion and tilt may not reflect the latest config.',
                );
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // On regime / transition / base-delta change: recompute the live delta.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchDynamicRiskAversion(
                    macroRegime,
                    isTransition,
                    baseDelta,
                );
                if (!cancelled) setLiveDelta(res.risk_aversion);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('Failed to fetch dynamic risk aversion', err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [macroRegime, isTransition, baseDelta]);

    const handleOptimize = async () => {
        setLoading(true);
        setSaveSuccess(false);
        setBuildError(null);
        try {
            // Thin-UI / fat-backend: ship only the dials + simulation config.
            // The backend hydrates real per-sleeve pricing + signals from the
            // DB, runs the pure 3-tier optimiser AND the Numba backtest, then
            // returns the real book + net equity curve + summary metrics.
            const res = await buildGlobalPortfolioLive({
                macro_regime_label: macroRegime,
                risk_profile: riskProfile,
                is_transition_imminent: isTransition,
                total_risky_budget: BUDGET_BY_PROFILE[riskProfile] ?? 1.0,
                base_delta: baseDelta,
                tilt_scalar: tiltScalar,
                simulation: {
                    initial_investment: investment,
                    additional_investment: additionalInvestment,
                    cash_flow_frequency: cashFlowFreq,
                    rebalance_frequency: rebalanceFreq,
                    tax_rate_percent: taxRatePct,
                    trading_fee_bps: feeBps,
                },
            });
            const rows = res.portfolio ?? [];
            setPortfolio(rows);
            setMacroWeights(deriveMacroWeights(rows));
            setSimulationMetrics(res.metrics ?? null);
            setEquityCurve(res.equity_curve ?? []);
            if (rows.length === 0) {
                setBuildError(
                    'The optimiser returned no holdings for the current market data.',
                );
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Live optimisation failed', err);
            const detail = (
                err as { response?: { data?: { detail?: string } } }
            )?.response?.data?.detail;
            setBuildError(
                detail || 'Optimisation failed. Please try again in a moment.',
            );
            setPortfolio([]);
            setMacroWeights([]);
            setSimulationMetrics(null);
            setEquityCurve([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveReport = async () => {
        if (!portfolio.length || !simulationMetrics || !equityCurve.length) return;
        if (!reportName.trim()) {
            setBuildError('Please enter a name for the strategy.');
            return;
        }
        setSavingReport(true);
        setBuildError(null);
        try {
            // Persist the book; the backend returns the new report's UUID.
            const response = await savePortfolioReport({
                report_name: reportName.trim(),
                macro_regime_label: macroRegime,
                risk_profile: riskProfile,
                is_transition_imminent: isTransition,
                total_risky_budget: BUDGET_BY_PROFILE[riskProfile] ?? 1.0,
                base_delta: baseDelta,
                tilt_scalar: tiltScalar,
                simulation: {
                    initial_investment: investment,
                    additional_investment: additionalInvestment,
                    cash_flow_frequency: cashFlowFreq,
                    rebalance_frequency: rebalanceFreq,
                    tax_rate_percent: taxRatePct,
                    trading_fee_bps: feeBps,
                },
                portfolio,
                metrics: simulationMetrics,
                equity_curve: equityCurve,
            });
            setSaveSuccess(true);
            setReportName('');
            // Hold the green "Saved!" badge for 1.2s, then route to the newly
            // created strategy's detail page (Tear Sheet) in the master hub.
            window.setTimeout(() => {
                setSaveSuccess(false);
                navigate(`/dashboard/my-strategies/${response.report_id}`);
            }, 1200);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to save report', err);
            setBuildError('Saving failed. Please try again.');
        } finally {
            setSavingReport(false);
        }
    };

    // Derived "wealth generation" hero metrics from the real Numba backtest.
    const derivedWealth = useMemo(() => {
        if (!simulationMetrics || equityCurve.length === 0) return null;
        const finalNetValue = simulationMetrics.final_net_value;
        const totalInvested = equityCurve[equityCurve.length - 1].total_invested;
        if (totalInvested <= 0) return null;
        const totalProfit = finalNetValue - totalInvested;
        const roi = (totalProfit / totalInvested) * 100;
        const multiple = finalNetValue / totalInvested;
        return {
            finalNetValue,
            totalInvested,
            totalProfit,
            roi,
            multiple,
            isPositive: totalProfit >= 0,
        };
    }, [simulationMetrics, equityCurve]);

    const cashPct = Math.round(
        (1 - (BUDGET_BY_PROFILE[riskProfile] ?? 1)) * 100,
    );

    const currency0 = (val: number): string =>
        val.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
        });

    return (
        <Layout
            sidebar={<Sidebar />}
            title="Multi-Asset Portfolio Optimizer"
            subtitle="Interactive 3-tier cross-asset engine — HRP micro, Black-Litterman macro, dynamic regime overlay."
        >
            {metaError && (
                <div className="surface-card-error mb-6 text-sm">{metaError}</div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-[340px_1fr] gap-6">
                {/* ============================================= */}
                {/* CONTROL RAIL (dark cockpit)                   */}
                {/* ============================================= */}
                <aside className="rounded-2xl bg-slate-900 text-slate-300 p-6 flex flex-col gap-7 shadow-lg h-fit xl:sticky xl:top-6">
                    <div>
                        <h2 className="text-lg font-bold text-white flex items-center gap-2 tracking-tight">
                            <BrainCircuitIcon className="w-5 h-5 text-indigo-400" />
                            Simulator
                        </h2>
                        <p className="text-[11px] text-slate-400 mt-1 uppercase tracking-wider font-semibold">
                            Control Panel
                        </p>
                    </div>

                    {/* Capital */}
                    <div>
                        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-2">
                            Initial Capital (USD)
                        </label>
                        <div className="relative">
                            <span className="absolute left-3 top-2.5 text-slate-500 font-bold">
                                $
                            </span>
                            <input
                                type="number"
                                step={10000}
                                min={0}
                                className="w-full bg-slate-800 border border-slate-700 rounded-lg py-2 pl-7 pr-3 text-white font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                value={investment}
                                onChange={(e) => setInvestment(Number(e.target.value))}
                            />
                        </div>
                    </div>

                    {/* Risk profile */}
                    <div>
                        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-2">
                            Strategic Risk Target
                        </label>
                        <select
                            className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white text-sm focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer"
                            value={riskProfile}
                            onChange={(e) => setRiskProfile(e.target.value)}
                        >
                            {RISK_PROFILES.map((p) => (
                                <option key={p} value={p}>
                                    {p}
                                </option>
                            ))}
                        </select>
                        {cashPct > 0 && (
                            <p className="text-[11px] text-slate-500 mt-1.5">
                                Holds ~{cashPct}% cash buffer at this risk band.
                            </p>
                        )}
                    </div>

                    {/* Tilt scalar */}
                    <div>
                        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex justify-between mb-2">
                            <span>ML Tilt Aggressiveness</span>
                            <span className="text-indigo-400 font-bold">
                                {tiltScalar.toFixed(2)}
                            </span>
                        </label>
                        <input
                            type="range"
                            min={0}
                            max={1}
                            step={0.05}
                            className="w-full accent-indigo-500 cursor-pointer"
                            value={tiltScalar}
                            onChange={(e) => setTiltScalar(Number(e.target.value))}
                        />
                    </div>

                    <div className="h-px bg-slate-800" />

                    {/* Backtest parameters (simulation dials) */}
                    <div>
                        <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">
                            Backtest Parameters
                        </h3>

                        {/* Periodic contribution */}
                        <div className="mb-3">
                            <label className="text-[10px] text-slate-500 block mb-1">
                                Periodic Contribution ($)
                            </label>
                            <div className="flex gap-2">
                                <div className="relative w-1/2">
                                    <span className="absolute left-3 top-2.5 text-slate-500 font-bold">
                                        $
                                    </span>
                                    <input
                                        type="number"
                                        step={500}
                                        min={0}
                                        className="w-full bg-slate-800 border border-slate-700 rounded-lg py-2 pl-7 pr-2 text-white font-mono text-xs focus:ring-2 focus:ring-indigo-500 outline-none disabled:opacity-50"
                                        value={additionalInvestment}
                                        onChange={(e) => setAdditionalInvestment(Number(e.target.value))}
                                        disabled={cashFlowFreq === 'none'}
                                    />
                                </div>
                                <select
                                    className="w-1/2 bg-slate-800 border border-slate-700 rounded-lg p-2 text-white text-xs outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                                    value={cashFlowFreq}
                                    onChange={(e) => setCashFlowFreq(e.target.value as CashFlowFrequency)}
                                >
                                    <option value="none">None</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="quarterly">Quarterly</option>
                                    <option value="semiannually">Semi-Annually</option>
                                    <option value="annually">Annually</option>
                                </select>
                            </div>
                        </div>

                        {/* Rebalancing strategy */}
                        <div className="mb-3">
                            <label className="text-[10px] text-slate-500 block mb-1">
                                Rebalancing Strategy
                            </label>
                            <select
                                className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2 text-white text-xs outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                                value={rebalanceFreq}
                                onChange={(e) => setRebalanceFreq(e.target.value as RebalanceFrequency)}
                            >
                                <option value="drift">Drift (Buy &amp; Hold)</option>
                                <option value="monthly">Monthly</option>
                                <option value="quarterly">Quarterly</option>
                                <option value="semiannually">Semi-Annually</option>
                                <option value="annually">Annually</option>
                            </select>
                        </div>

                        {/* Drag: taxes & fees */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] text-slate-500 block mb-1">
                                    Taxes (CGT %)
                                </label>
                                <input
                                    type="number"
                                    step={0.5}
                                    max={50}
                                    min={0}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2 text-white text-xs font-mono outline-none focus:ring-2 focus:ring-indigo-500"
                                    value={taxRatePct}
                                    onChange={(e) => setTaxRatePct(Number(e.target.value))}
                                />
                            </div>
                            <div>
                                <label className="text-[10px] text-slate-500 block mb-1">
                                    Trading Fee (bps)
                                </label>
                                <input
                                    type="number"
                                    step={1}
                                    min={0}
                                    max={200}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2 text-white text-xs font-mono outline-none focus:ring-2 focus:ring-indigo-500"
                                    value={feeBps}
                                    onChange={(e) => setFeeBps(Number(e.target.value))}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="h-px bg-slate-800" />

                    {/* Macro regime */}
                    <div>
                        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                            Macro Regime Overlay
                            <span className="group relative flex items-center">
                                <InfoIcon className="w-3.5 h-3.5 text-slate-500 cursor-help hover:text-indigo-400" />
                                <span className="absolute left-6 top-0 hidden group-hover:block w-52 p-2 bg-slate-800 text-[11px] text-slate-300 rounded shadow-xl z-50 border border-slate-700 normal-case tracking-normal">
                                    Tier-3 Ilmanen business-cycle regime. Dynamically
                                    scales the Black-Litterman risk aversion (δ).
                                </span>
                            </span>
                        </label>
                        <select
                            className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white text-sm focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer"
                            value={macroRegime}
                            onChange={(e) => setMacroRegime(e.target.value)}
                        >
                            {MACRO_REGIMES.map((r) => (
                                <option key={r.value} value={r.value}>
                                    {r.label}
                                </option>
                            ))}
                        </select>

                        <label className="flex items-center gap-3 mt-4 p-3 bg-slate-950/50 rounded-lg border border-slate-800 cursor-pointer group">
                            <input
                                type="checkbox"
                                className="w-4 h-4 accent-indigo-600 cursor-pointer"
                                checked={isTransition}
                                onChange={(e) => setIsTransition(e.target.checked)}
                            />
                            <span className="flex flex-col">
                                <span className="text-xs font-semibold text-slate-300 group-hover:text-white transition-colors">
                                    Imminent AFML Transition
                                </span>
                                <span className="text-[10px] text-slate-500">
                                    Floors the risk-aversion multiplier
                                </span>
                            </span>
                        </label>

                        {/* Live delta readout */}
                        <div className="mt-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-3 flex justify-between items-center">
                            <span className="text-[11px] font-semibold text-indigo-300 uppercase tracking-wider">
                                Live Delta (δ)
                            </span>
                            <span className="text-lg font-mono text-emerald-400 font-bold">
                                {liveDelta.toFixed(2)}
                            </span>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={handleOptimize}
                        disabled={loading}
                        className="mt-2 w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 rounded-lg transition-colors flex justify-center items-center gap-2 shadow-lg shadow-indigo-900/30 disabled:opacity-70"
                    >
                        {loading ? (
                            <LoaderIcon className="w-5 h-5 animate-spin" />
                        ) : (
                            <Settings2Icon className="w-5 h-5" />
                        )}
                        {loading ? 'Solving Matrices…' : 'Generate Portfolio'}
                    </button>
                </aside>

                {/* ============================================= */}
                {/* OUTPUT AREA                                   */}
                {/* ============================================= */}
                <div className="min-w-0 space-y-6">
                    {buildError && (
                        <div className="surface-card-error text-sm">
                            {buildError}
                        </div>
                    )}
                    {hasResult ? (
                        <>
                            {/* ============================================ */}
                            {/* TIER 1: THE WEALTH GENERATION HERO BANNER    */}
                            {/* ============================================ */}
                            {derivedWealth && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {/* Total profit (glowing) */}
                                    <div className={`relative overflow-hidden rounded-2xl p-6 border shadow-lg ${derivedWealth.isPositive ? 'bg-emerald-950/20 border-emerald-500/30' : 'bg-red-950/20 border-red-500/30'}`}>
                                        <div className={`absolute -right-6 -top-6 w-24 h-24 blur-3xl opacity-20 rounded-full ${derivedWealth.isPositive ? 'bg-emerald-500' : 'bg-red-500'}`} />
                                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1 relative z-10">
                                            Total Net Profit Generated
                                        </h4>
                                        <p className={`text-4xl md:text-5xl font-black tracking-tighter font-mono relative z-10 ${derivedWealth.isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {derivedWealth.isPositive ? '+' : ''}
                                            {currency0(derivedWealth.totalProfit)}
                                        </p>
                                        <p className="text-xs text-slate-500 mt-2 font-medium relative z-10">
                                            After all simulated taxes &amp; trading fees.
                                        </p>
                                    </div>

                                    {/* Final wealth */}
                                    <div className="rounded-2xl p-6 border bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-lg flex flex-col justify-center">
                                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                                            Final Portfolio Value
                                        </h4>
                                        <p className="text-3xl md:text-4xl font-black tracking-tighter text-slate-900 dark:text-white font-mono">
                                            {currency0(derivedWealth.finalNetValue)}
                                        </p>
                                        <p className="text-xs text-slate-500 mt-2 font-medium">
                                            From {currency0(derivedWealth.totalInvested)} total capital invested.
                                        </p>
                                    </div>

                                    {/* ROI & MOIC */}
                                    <div className="rounded-2xl p-6 border bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-lg flex flex-col justify-center">
                                        <div className="flex justify-between items-end mb-1">
                                            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                                Return on Investment
                                            </h4>
                                            <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 px-2 py-0.5 rounded">
                                                {derivedWealth.multiple.toFixed(2)}x MOIC
                                            </span>
                                        </div>
                                        <p className={`text-3xl md:text-4xl font-black tracking-tighter font-mono ${derivedWealth.isPositive ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
                                            {derivedWealth.isPositive ? '+' : ''}
                                            {derivedWealth.roi.toFixed(1)}%
                                        </p>
                                        <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-1.5 mt-4 overflow-hidden">
                                            <div
                                                className={`h-1.5 rounded-full ${derivedWealth.isPositive ? 'bg-emerald-500' : 'bg-red-500'}`}
                                                style={{ width: `${Math.min(100, Math.max(0, derivedWealth.roi))}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ============================================ */}
                            {/* TIER 2: THE QUANT RISK BANNER                */}
                            {/* ============================================ */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <KpiCard
                                    label="CAGR (Net)"
                                    value={simulationMetrics ? `${(simulationMetrics.net_expected_return_ann * 100).toFixed(2)}%` : '--'}
                                    tone="bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400"
                                    icon={<TrendingUpIcon className="w-5 h-5" />}
                                />
                                <KpiCard
                                    label="Sharpe Ratio (Net)"
                                    value={simulationMetrics ? simulationMetrics.net_sharpe.toFixed(2) : '--'}
                                    tone="bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400"
                                    icon={<ActivityIcon className="w-5 h-5" />}
                                />
                                <KpiCard
                                    label="Max Drawdown"
                                    value={simulationMetrics ? `${(simulationMetrics.max_drawdown * 100).toFixed(2)}%` : '--'}
                                    tone="bg-red-50 text-red-600 dark:bg-red-950/20 dark:text-red-400"
                                    icon={<ShieldAlertIcon className="w-5 h-5" />}
                                />
                                <KpiCard
                                    label="Tax & Fee Drag"
                                    value={simulationMetrics ? currency0(simulationMetrics.total_taxes_paid + simulationMetrics.total_fees_paid) : '--'}
                                    tone="bg-amber-50 text-amber-600 dark:bg-amber-950/20 dark:text-amber-400"
                                    icon={<DatabaseIcon className="w-5 h-5" />}
                                />
                            </div>

                            {/* ============================================ */}
                            {/* CHARTS: Donut + Net Equity Curve             */}
                            {/* ============================================ */}
                            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                                <div className="surface-card flex flex-col xl:col-span-1">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="section-title">Macro Allocation</h3>
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                                            Tier-2 B-L
                                        </span>
                                    </div>
                                    <div className="flex-1 min-h-[300px]">
                                        <MacroAllocationDonut data={macroWeights} />
                                    </div>
                                </div>

                                <div className="surface-card flex flex-col xl:col-span-2">
                                    <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
                                        <div>
                                            <h3 className="section-title text-lg">
                                                Historical Net Equity Curve (TWRR)
                                            </h3>
                                            <p className="muted text-xs mt-1">
                                                <span className="text-emerald-500 font-semibold">Green</span>{' '}
                                                = managed ML portfolio (net).{' '}
                                                <span className="text-amber-500 font-semibold">Amber</span>{' '}
                                                = 4% savings account.{' '}
                                                <span className="text-slate-400 font-semibold">Dashed</span>{' '}
                                                = capital invested.
                                            </p>
                                        </div>
                                        {simulationMetrics && (
                                            <div className="flex gap-4 text-right">
                                                <div>
                                                    <p className="muted text-[10px] font-bold uppercase tracking-wider">
                                                        Taxes Paid
                                                    </p>
                                                    <p className="text-sm font-mono font-bold text-amber-600 dark:text-amber-400">
                                                        {currency0(simulationMetrics.total_taxes_paid)}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="muted text-[10px] font-bold uppercase tracking-wider">
                                                        Fees Paid
                                                    </p>
                                                    <p className="text-sm font-mono font-bold text-amber-600 dark:text-amber-400">
                                                        {currency0(simulationMetrics.total_fees_paid)}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <HistoricalBacktestChart data={equityCurve} />
                                </div>
                            </div>

                            {/* Quant X-Ray */}
                            <div className="surface-card">
                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                                    <div>
                                        <h3 className="section-title">
                                            Quant X-Ray: Target Holdings
                                        </h3>
                                        <p className="muted text-xs mt-1">
                                            Mathematical decomposition driving the final
                                            allocation (Tier-1 × Tier-2).
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-3">
                                        {saveSuccess && (
                                            <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-1 rounded">
                                                <CheckCircleIcon className="w-4 h-4" />
                                                Saved Successfully!
                                            </span>
                                        )}
                                        <input
                                            type="text"
                                            placeholder="Name this Strategy…"
                                            value={reportName}
                                            onChange={(e) => setReportName(e.target.value)}
                                            className="text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 px-3 py-2 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 text-slate-900 dark:text-white"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleSaveReport}
                                            disabled={savingReport || !reportName.trim() || !simulationMetrics || !equityCurve.length}
                                            className="text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-500 px-4 py-2 rounded-lg transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50"
                                        >
                                            {savingReport ? (
                                                <LoaderIcon className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <SaveIcon className="w-4 h-4" />
                                            )}
                                            Save Strategy
                                        </button>
                                    </div>
                                </div>
                                <QuantXRayTable
                                    rows={portfolio}
                                    investment={investment}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="surface-card flex flex-col items-center justify-center text-center py-20 min-h-[520px] border-dashed">
                            <div className="w-20 h-20 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mb-6 border border-slate-200 dark:border-slate-700 text-slate-400">
                                <DatabaseIcon className="w-9 h-9" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2 tracking-tight">
                                Awaiting Parameters
                            </h3>
                            <p className="muted text-sm max-w-md leading-relaxed">
                                Set your capital, strategic risk target and macro-regime
                                overlay in the control panel, then click
                                {' '}
                                <span className="font-semibold text-slate-700 dark:text-slate-300">
                                    Generate Portfolio
                                </span>
                                {' '}
                                to run the 3-tier engine.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default MultiAssetOptimizerPage;
